import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

import { getCurrentLang, translate } from "@/i18n";
import { getAuthToken, serverUrl } from "@/lib/db";
import { mapCartItemsToProductInfo } from "@/modules/ecommerce/cart/mapCartItems";

const EcommerceBundle = React.lazy(() => import("@/modules/ecommerce").then(m => ({ default: m.EcommerceBundle })));

export const EcommerceBundleWrapper = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const locale = getCurrentLang();

    // Memoize dependencies to prevent re-renders
    const dependencies = React.useMemo(() => {
        return {
            user: user ? {
                id: user.id,
                email: user.email,
                user_metadata: {
                    display_name: user.user_metadata?.display_name
                }
            } : null,
            toast: {
                success: (msg: string) => import('sonner').then(s => s.toast.success(msg)),
                error: (msg: string) => import('sonner').then(s => s.toast.error(msg))
            },
            onFetchAddresses: async (userId: string) => {
                const { getShippingAddresses } = await import('@/modules/user/client-user');
                return getShippingAddresses(userId) as Promise<any[]>;
            },
            onSaveAddress: async (userId: string, addresses: any[]) => {
                const { saveShippingAddresses } = await import('@/modules/user/client-user');
                await saveShippingAddresses(userId, addresses);
            },
            onPlaceOrder: async (data: any) => {
                const { useCartStore } = await import('@/modules/ecommerce');
                const { createTransaction } = await import('@/modules/ecommerce/client-ecommerce');
                const cartItems = useCartStore.getState().items;
                const cartSubtotal = useCartStore.getState().subtotal;

                const items = Array.isArray(data.product_info) && data.product_info.length > 0
                    ? data.product_info
                    : cartItems;

                const tx = await createTransaction({
                    shipping_info: data.shipping,
                    vendor_info: {},
                    product_info: mapCartItemsToProductInfo(items),
                    total_amount: data.total_amount ?? cartSubtotal,
                    currency: data.currency ?? 'eur',
                    payment_provider: data.paymentMethod,
                    external_order_id: data.external_order_id,
                    metadata: data.metadata,
                });

                if (cartItems.length > 0) {
                    useCartStore.getState().clearCart();
                }
                return tx;
            },
            onFinalizeStripeOrder: async (
                transactionId: string,
                data: { shipping: Record<string, unknown>; paymentIntent?: { id: string; status: string } },
            ) => {
                const { useCartStore } = await import('@/modules/ecommerce');
                const { updateTransaction } = await import('@/modules/ecommerce/client-ecommerce');
                await updateTransaction(transactionId, {
                    shipping_info: data.shipping,
                    metadata: {
                        stripe_payment_intent_id: data.paymentIntent?.id,
                        stripe_status: data.paymentIntent?.status,
                    },
                });
                useCartStore.getState().clearCart();
            },
            onUpdateTransaction: async (id: string, patch: Record<string, unknown>) => {
                const { updateTransaction } = await import('@/modules/ecommerce/client-ecommerce');
                return updateTransaction(id, patch as any);
            },
            onFetchTransactions: async () => {
                const { listTransactions } = await import('@/modules/ecommerce/client-ecommerce');
                return listTransactions();
            },
            onFetchTransaction: async (id: string) => {
                const { getTransaction } = await import('@/modules/ecommerce/client-ecommerce');
                return getTransaction(id);
            },
            onNavigate: (path: string) => navigate(path),
            siteName: "PolyMech",
            contactEmail: "sales@polymech.info",
            stripePublishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
            apiBaseUrl: serverUrl,
            getAuthToken: async () => (await getAuthToken()) ?? null,
            locale,
            t: translate,
            impressum: {
                company: "Polymech S.L.",
                representative: "Guenter Volkmar Baumgart",
                address: "Poligono Can Clapers, Carrer Can Peric 11, 1B, 08181 Sentmenat, Barcelona, Spain",
                email: "info@polymech.info",
                phone: "+34 691 952 287",
                vatId: "ES-Y0100830N",
                register: "Registro Mercantil de Barcelona",
                responsibleContent: "Guenter Volkmar Baumgart (§ 18 Abs. 2 MStV)",
            },
        };
    }, [user, navigate, locale]);

    return (
        <React.Suspense fallback={<div className="flex items-center justify-center p-12">Loading...</div>}>
            <EcommerceBundle {...dependencies} />
        </React.Suspense>
    );
};
